import { useEffect, useMemo, useState } from "react";
export { readOrderHandoverRosterReturnState, useOrderHandoverRosterReturn } from "./useOrderHandoverRosterReturn";
export type { OrderHandoverRosterReturnState, OrderHandoverRosterViewState } from "./useOrderHandoverRosterReturn";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isHttpClientError } from "../../../lib/http/errors";

import {
  applyOrderHandoverContract,
  commitOrderHandoverRosterImport,
  createOrderHandoverDraftOrder as createOrderHandoverDraftOrderRequest,
  createOrderHandoverRosterMember,
  exportOrderHandoverRoster,
  fetchOrderHandoverCustomerQuoteOptions,
  fetchOrderHandoverSalesMetadata,
  fetchOrderHandoverMajorOptions,
  fetchOrderHandoverDetail,
  previewOrderHandoverRosterImport,
  selectOrderHandoverCustomerQuote,
  publishOrderHandoverContract,
  unpublishOrderHandoverContract,
  unpublishAllOrderHandoverLocks,
  invalidateOrderHandoverContract,
  issueOrderHandoverContractSignInvitations,
  resendOrderHandoverContractSignSms,
  selectOrderHandoverItinerary,
  updateOrderHandoverRosterFinalize,
  updateOrderHandoverRosterMember,
  updateOrderHandoverRosterMemberFlags,
  updateOrderHandoverRosterMemberStop,
  updateOrderHandoverRosterMemberStops,
  updateOrderHandoverSalesFinalize,
  updateOrderHandoverSalesHandover,
} from "../repo";
import type {
  HandoverItineraryOption,
  OrderHandoverContractApplyPayload,
  OrderHandoverContractInvalidatePayload,
  OrderHandoverContractSigning,
  OrderHandoverRosterExportType,
  OrderHandoverRosterExportOptions,
  OrderHandoverRosterImportPayload,
  OrderHandoverRouteContext,
  OrderHandoverRosterMemberCreatePayload,
  OrderHandoverRosterMemberFlagsPayload,
  OrderHandoverRosterMemberStopsPayload,
  OrderHandoverRosterMemberUpdatePayload,
  OrderHandoverSalesHandoverUpdate,
} from "../types";
import type { OrderHandoverRosterViewState } from "./useOrderHandoverRosterReturn";

/** 合同签订状态轮询间隔（毫秒）。 */
const CONTRACT_SIGNING_POLL_INTERVAL_MS = 3_000;

/** 挂网/申请后等待合同快照出现的短时轮询窗口。 */
const CONTRACT_SIGNING_BOOTSTRAP_POLL_MS = 45_000;

type OrderHandoverRuntimeOptions = {
  onRosterImportSuccess?: () => void;
  initialRosterView?: OrderHandoverRosterViewState;
};

/**
 * 详情页合同签订卡片是否需要异步轮询。
 * 仍有未签且未作废的签署人，或作废待同意时继续刷；全部签署完成或合同已终态则停止。
 */
export function shouldPollContractSigningStatus(
  signing: OrderHandoverContractSigning | undefined | null,
): boolean {
  if (signing == null || !signing.contractNo || signing.signRecords.length === 0) {
    return false;
  }
  if (
    signing.signStateCode === "void_applying" ||
    signing.signStateCode === "partial_voided"
  ) {
    return true;
  }
  if (
    signing.signStateCode === "all_signed" ||
    signing.signStateCode === "voided" ||
    signing.signStateCode === "invalid"
  ) {
    return false;
  }
  if (signing.signRecords.every((record) => record.isSign || record.isCancelled)) {
    return false;
  }
  return signing.signRecords.some((record) => !record.isSign && !record.isCancelled);
}

/** 为其他功能域提供创建或复用订单草稿的业务动作，不暴露仓储实现。 */
export async function createOrderHandoverDraftOrder(projectId: string) {
  return createOrderHandoverDraftOrderRequest(projectId);
}

/** 从路由和查询参数整理订单交接详情页上下文。 */
export function buildOrderHandoverRouteContext(
  projectId: string | undefined,
  searchParams: URLSearchParams,
): OrderHandoverRouteContext {
  return {
    projectId: projectId?.trim() || "project-demo",
    projectCode: searchParams.get("projectCode")?.trim() || undefined,
    legacyId: searchParams.get("legacyId")?.trim() || undefined,
    version: searchParams.get("version")?.trim() || undefined,
    articleId: searchParams.get("articleId")?.trim() || undefined,
  };
}

/** 管理订单交接详情页的本地交互状态。 */
export function useOrderHandoverRuntime(
  context: OrderHandoverRouteContext,
  options: OrderHandoverRuntimeOptions = {},
) {
  const queryClient = useQueryClient();
  const [selectedItinerary, setSelectedItinerary] = useState<HandoverItineraryOption | undefined>();
  const [pendingItinerary, setPendingItinerary] = useState<HandoverItineraryOption | undefined>();
  const [itineraryDrawerVisible, setItineraryDrawerVisible] = useState(false);
  const [salesEditVisible, setSalesEditVisible] = useState(false);
  const [rosterKeyword, setRosterKeyword] = useState(options.initialRosterView?.keyword ?? "");
  const [rosterTypeFilter, setRosterTypeFilter] = useState(options.initialRosterView?.type ?? "全部");
  const [rosterClassGroupFilter, setRosterClassGroupFilter] = useState(options.initialRosterView?.classGroup ?? "全部");
  const [rosterStoppedFilter, setRosterStoppedFilter] = useState(options.initialRosterView?.stopped ?? "active");
  /** 生成/挂网后短时轮询，等待合同快照写入详情。 */
  const [contractSigningPollUntilMs, setContractSigningPollUntilMs] = useState<number | null>(null);
  const detailQueryKey = useMemo(
    () => ["order_handover", "detail", context.projectId] as const,
    [context.projectId],
  );
  const beginContractSigningAutoRefresh = (refreshImmediately = true) => {
    setContractSigningPollUntilMs(Date.now() + CONTRACT_SIGNING_BOOTSTRAP_POLL_MS);
    if (refreshImmediately) {
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    }
  };
  const detailQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => fetchOrderHandoverDetail(context.projectId),
    retry: false,
    refetchInterval: (query) => {
      const signing = query.state.data?.contractSigning;
      if (shouldPollContractSigningStatus(signing)) {
        return CONTRACT_SIGNING_POLL_INTERVAL_MS;
      }
      // 挂网/申请后合同可能尚未出现在详情里，短时继续刷到快照就绪或窗口结束。
      if (
        contractSigningPollUntilMs != null &&
        Date.now() < contractSigningPollUntilMs &&
        (signing == null || !signing.contractNo)
      ) {
        return CONTRACT_SIGNING_POLL_INTERVAL_MS;
      }
      return false;
    },
  });
  const salesMetadataQuery = useQuery({
    queryKey: ["order_handover", "sales_metadata", context.projectId],
    queryFn: () => fetchOrderHandoverSalesMetadata(context.projectId),
    staleTime: 5 * 60 * 1000,
  });
  const majorOptionsQuery = useQuery({
    queryKey: ["order_handover", "major_options"],
    queryFn: fetchOrderHandoverMajorOptions,
    staleTime: 5 * 60 * 1000,
  });
  const customerQuoteOptionsQuery = useQuery({
    queryKey: ["order_handover", "customer_quote_options", context.projectId, detailQuery.data?.selectedItinerary.id],
    queryFn: () => fetchOrderHandoverCustomerQuoteOptions(context.projectId),
    enabled: Boolean(detailQuery.data?.selectedItinerary.id),
    retry: false,
  });
  const updateSalesMutation = useMutation({
    mutationFn: (payload: OrderHandoverSalesHandoverUpdate) =>
      updateOrderHandoverSalesHandover(context.projectId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      void queryClient.invalidateQueries({
        queryKey: ["order_handover", "customer_quote_options", context.projectId],
      });
      setSalesEditVisible(false);
      setPendingItinerary(undefined);
    },
  });
  const selectCustomerQuoteMutation = useMutation({
    mutationFn: (customerQuoteId: string) =>
      selectOrderHandoverCustomerQuote(context.projectId, customerQuoteId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const salesFinalizeMutation = useMutation({
    mutationFn: (finalized: boolean) =>
      updateOrderHandoverSalesFinalize(context.projectId, finalized),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const contractApplyMutation = useMutation({
    mutationFn: (payload: OrderHandoverContractApplyPayload | undefined) =>
      applyOrderHandoverContract(context.projectId, payload ?? {}),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      beginContractSigningAutoRefresh();
    },
    onError() {
      // 已有签署中合同时接口会 409；仍刷新详情，让签订卡片展示当前合同与签署进度。
      beginContractSigningAutoRefresh();
    },
  });
  const contractSignInviteMutation = useMutation({
    mutationFn: () => issueOrderHandoverContractSignInvitations(context.projectId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const contractSignResendSmsMutation = useMutation({
    mutationFn: (signRecordId: string) =>
      resendOrderHandoverContractSignSms(context.projectId, signRecordId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const contractPublishMutation = useMutation({
    mutationFn: () => publishOrderHandoverContract(context.projectId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      // 挂网接口已返回最新详情，先展示合同签订卡片，再从下一轮开始刷新，
      // 避免立即请求把刚写入的最新状态短暂覆盖成旧快照。
      beginContractSigningAutoRefresh(false);
    },
  });
  const contractUnpublishMutation = useMutation({
    mutationFn: () => unpublishOrderHandoverContract(context.projectId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      beginContractSigningAutoRefresh();
    },
    onError() {
      beginContractSigningAutoRefresh();
    },
  });
  const contractUnpublishAllMutation = useMutation({
    mutationFn: () => unpublishAllOrderHandoverLocks(context.projectId),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      beginContractSigningAutoRefresh();
    },
    onError() {
      beginContractSigningAutoRefresh();
    },
  });
  const contractInvalidateMutation = useMutation({
    mutationFn: (payload: OrderHandoverContractInvalidatePayload | undefined) =>
      invalidateOrderHandoverContract(context.projectId, payload ?? {}),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      beginContractSigningAutoRefresh();
    },
    onError() {
      beginContractSigningAutoRefresh();
    },
  });
  const selectItineraryMutation = useMutation({
    mutationFn: async (option: HandoverItineraryOption) => {
      const nextDetail = await selectOrderHandoverItinerary(context.projectId, option.id);
      queryClient.setQueryData(detailQueryKey, nextDetail);
      // 等新行程报价候选写入缓存后再结束 pending，避免转圈先停、报价空白一阵。
      await queryClient.fetchQuery({
        queryKey: [
          "order_handover",
          "customer_quote_options",
          context.projectId,
          nextDetail.selectedItinerary.id,
        ],
        queryFn: () => fetchOrderHandoverCustomerQuoteOptions(context.projectId),
      });
      return nextDetail;
    },
    onSuccess() {
      setSelectedItinerary(undefined);
      // 保留 pending：销售交接继续合并行程有值字段；保存草稿时对同一行程幂等再提交。
      setItineraryDrawerVisible(false);
    },
  });
  const rosterImportPreviewMutation = useMutation({
    mutationFn: (payload: OrderHandoverRosterImportPayload) =>
      previewOrderHandoverRosterImport(context.projectId, payload),
  });
  const rosterImportCommitMutation = useMutation({
    mutationFn: (payload: OrderHandoverRosterImportPayload) =>
      commitOrderHandoverRosterImport(context.projectId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
      rosterImportPreviewMutation.reset();
      options.onRosterImportSuccess?.();
    },
  });
  const rosterFlagsMutation = useMutation({
    mutationFn: (payload: OrderHandoverRosterMemberFlagsPayload) =>
      updateOrderHandoverRosterMemberFlags(context.projectId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterStopMutation = useMutation({
    mutationFn: ({ memberId, isStopped }: { memberId: string; isStopped: boolean }) =>
      updateOrderHandoverRosterMemberStop(context.projectId, memberId, isStopped),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterStopsMutation = useMutation({
    mutationFn: (payload: OrderHandoverRosterMemberStopsPayload) =>
      updateOrderHandoverRosterMemberStops(context.projectId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterMemberMutation = useMutation({
    mutationFn: ({
      memberId,
      payload,
    }: {
      memberId: string;
      payload: OrderHandoverRosterMemberUpdatePayload;
    }) => updateOrderHandoverRosterMember(context.projectId, memberId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterCreateMutation = useMutation({
    mutationFn: (payload: OrderHandoverRosterMemberCreatePayload) =>
      createOrderHandoverRosterMember(context.projectId, payload),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterFinalizeMutation = useMutation({
    mutationFn: (finalized: boolean) =>
      updateOrderHandoverRosterFinalize(context.projectId, finalized),
    onSuccess(nextDetail) {
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
  });
  const rosterExportMutation = useMutation({
    mutationFn: ({ exportType, options }: {
      exportType: OrderHandoverRosterExportType;
      options?: OrderHandoverRosterExportOptions;
    }) => options === undefined
      ? exportOrderHandoverRoster(context.projectId, exportType)
      : exportOrderHandoverRoster(context.projectId, exportType, options),
  });

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel("order-handover-roster-import");
    channel.onmessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; projectId?: string };
      if (payload.type !== "order-handover-roster-imported" || payload.projectId !== context.projectId) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    };
    return () => {
      channel.close();
    };
  }, [context.projectId, detailQueryKey, queryClient]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel("order-handover-contract-signing");
    channel.onmessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string; projectId?: string };
      if (
        payload.type !== "order-handover-contract-signed" ||
        payload.projectId !== context.projectId
      ) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
    };
    return () => {
      channel.close();
    };
  }, [context.projectId, detailQueryKey, queryClient]);

  const orderNo = context.legacyId || context.projectCode || "ORD-2026-001";
  const detail = detailQuery.data;
  const itineraryOptions = detail?.itineraryOptions ?? [];
  const detailSelectedItinerary =
    detail?.selectedItinerary.id ? detail.selectedItinerary : detail?.itineraryOptions[0];
  const displayItinerary =
    pendingItinerary ?? selectedItinerary ?? detailSelectedItinerary ?? detail?.selectedItinerary;
  const rosterStats = useMemo(
    () =>
      detail?.roster.stats ?? {
        total: 0,
        entered: 0,
        student: displayItinerary?.studentCount ?? 0,
        teacher: displayItinerary?.teacherCount ?? 0,
        enteredStudent: 0,
        enteredTeacher: 0,
        guide: displayItinerary?.guideCount ?? 0,
      },
    [detail?.roster.stats, displayItinerary],
  );

  return {
    context,
    detail,
    detailLoading: detailQuery.isLoading,
    detailErrorMessage: detailQuery.error ? "订单交接详情加载失败，请稍后重试。" : "",
    orderNo: detail?.summary.orderNo ?? orderNo,
    selectedItinerary: displayItinerary,
    pendingItinerary,
    itineraryOptions,
    rosterMembers: detail?.roster.members ?? [],
    roster: detail?.roster,
    rosterStats,
    summary: detail?.summary,
    salesHandover: detail?.salesHandover,
    contractSigning: detail?.contractSigning,
    orderLines: detail?.orderLines ?? [],
    selectedCustomerQuoteId: detail?.selectedCustomerQuoteId,
    customerQuoteOptions: customerQuoteOptionsQuery.data ?? [],
    customerQuoteOptionsLoading: customerQuoteOptionsQuery.isLoading,
    customerQuoteSelecting: selectCustomerQuoteMutation.isPending,
    customerQuoteErrorMessage: formatCustomerQuoteError(
      selectCustomerQuoteMutation.error || customerQuoteOptionsQuery.error,
    ),
    serviceConsultantOptions: salesMetadataQuery.data?.serviceConsultantOptions ?? [],
    staffOptions: salesMetadataQuery.data?.staffOptions ?? [],
    schoolSiteOptions: salesMetadataQuery.data?.schoolSiteOptions ?? [],
    staffOptionsLoading: salesMetadataQuery.isLoading,
    majorOptions: majorOptionsQuery.data ?? [],
    majorOptionsLoading: majorOptionsQuery.isLoading,
    itineraryDrawerVisible,
    salesEditVisible,
    salesSaving: updateSalesMutation.isPending || selectItineraryMutation.isPending,
    salesFinalizing: salesFinalizeMutation.isPending,
    contractApplying: contractApplyMutation.isPending,
    contractSignInviting: contractSignInviteMutation.isPending,
    contractSignResendingSms: contractSignResendSmsMutation.isPending,
    contractPublishing: contractPublishMutation.isPending,
    contractUnpublishing: contractUnpublishMutation.isPending,
    contractUnpublishingAll: contractUnpublishAllMutation.isPending,
    contractInvalidating: contractInvalidateMutation.isPending,
    itinerarySelecting: selectItineraryMutation.isPending,
    salesSaveErrorMessage:
      updateSalesMutation.error || selectItineraryMutation.error
        ? "合同交接信息保存失败，请稍后重试。"
        : "",
    itinerarySelectErrorMessage: selectItineraryMutation.error ? "行程选择保存失败，请稍后重试。" : "",
    rosterImportPreview: rosterImportPreviewMutation.data,
    rosterImportPreviewing: rosterImportPreviewMutation.isPending,
    rosterImportCommitting: rosterImportCommitMutation.isPending,
    rosterImportErrorMessage: formatRosterImportError(
      rosterImportPreviewMutation.error || rosterImportCommitMutation.error,
    ),
    rosterKeyword,
    rosterTypeFilter,
    rosterClassGroupFilter,
    rosterStoppedFilter,
    rosterFlagsSaving: rosterFlagsMutation.isPending,
    rosterMemberSaving: rosterMemberMutation.isPending,
    rosterMemberCreating: rosterCreateMutation.isPending,
    rosterStopSaving: rosterStopMutation.isPending || rosterStopsMutation.isPending,
    rosterFinalizing: rosterFinalizeMutation.isPending,
    rosterExporting: rosterExportMutation.isPending,
    openItineraryDrawer() {
      setItineraryDrawerVisible(true);
    },
    closeItineraryDrawer() {
      setItineraryDrawerVisible(false);
    },
    selectItinerary(option: HandoverItineraryOption) {
      if (detail) {
        setPendingItinerary(option);
        setSelectedItinerary(undefined);
        setItineraryDrawerVisible(false);
        setSalesEditVisible(true);
        // 立即落库签约行程，使合同快照与成交报价候选按新行程刷新；销售交接字段仍待保存草稿确认。
        void selectItineraryMutation.mutateAsync(option);
        return;
      }
      setSelectedItinerary(option);
      setItineraryDrawerVisible(false);
    },
    openSalesEdit() {
      setSalesEditVisible(true);
    },
    closeSalesEdit() {
      setSalesEditVisible(false);
      setPendingItinerary(undefined);
    },
    saveSalesHandover(payload: OrderHandoverSalesHandoverUpdate) {
      updateSalesMutation.mutate(payload);
    },
    updateSalesFinalize(finalized: boolean) {
      salesFinalizeMutation.mutate(finalized);
    },
    updateSalesFinalizeAsync(finalized: boolean) {
      return salesFinalizeMutation.mutateAsync(finalized);
    },
    applyContract(payload?: OrderHandoverContractApplyPayload) {
      contractApplyMutation.mutate(payload);
    },
    applyContractAsync(payload?: OrderHandoverContractApplyPayload) {
      return contractApplyMutation.mutateAsync(payload);
    },
    refreshContractSigningAsync() {
      beginContractSigningAutoRefresh();
      return queryClient.refetchQueries({ queryKey: detailQueryKey });
    },
    issueSignInvitationsAsync() {
      return contractSignInviteMutation.mutateAsync();
    },
    resendSignSmsAsync(signRecordId: string) {
      return contractSignResendSmsMutation.mutateAsync(signRecordId);
    },
    publishContract() {
      contractPublishMutation.mutate();
    },
    publishContractAsync() {
      return contractPublishMutation.mutateAsync();
    },
    unpublishContract() {
      contractUnpublishMutation.mutate();
    },
    unpublishContractAsync() {
      return contractUnpublishMutation.mutateAsync();
    },
    unpublishAllLocks() {
      contractUnpublishAllMutation.mutate();
    },
    unpublishAllLocksAsync() {
      return contractUnpublishAllMutation.mutateAsync();
    },
    invalidateContract(payload?: OrderHandoverContractInvalidatePayload) {
      contractInvalidateMutation.mutate(payload);
    },
    invalidateContractAsync(payload?: OrderHandoverContractInvalidatePayload) {
      return contractInvalidateMutation.mutateAsync(payload);
    },
    async savePendingItinerary() {
      if (!pendingItinerary) {
        return;
      }
      const nextDetail = await selectItineraryMutation.mutateAsync(pendingItinerary);
      queryClient.setQueryData(detailQueryKey, nextDetail);
    },
    selectCustomerQuote(customerQuoteId: string) {
      selectCustomerQuoteMutation.mutate(customerQuoteId);
    },
    previewRosterImport(payload: OrderHandoverRosterImportPayload) {
      rosterImportPreviewMutation.mutate(payload);
    },
    resetRosterImportPreview() {
      rosterImportPreviewMutation.reset();
    },
    commitRosterImport(payload: OrderHandoverRosterImportPayload) {
      rosterImportCommitMutation.mutate(payload);
    },
    updateRosterFlags(payload: OrderHandoverRosterMemberFlagsPayload) {
      rosterFlagsMutation.mutate(payload);
    },
    updateRosterFlagsAsync(payload: OrderHandoverRosterMemberFlagsPayload) {
      return rosterFlagsMutation.mutateAsync(payload);
    },
    updateRosterStop(memberId: string, isStopped: boolean) {
      rosterStopMutation.mutate({ memberId, isStopped });
    },
    async updateRosterStopsAsync(memberIds: string[], isStopped: boolean) {
      await rosterStopsMutation.mutateAsync({ memberIds, isStopped });
    },
    updateRosterMember(memberId: string, payload: OrderHandoverRosterMemberUpdatePayload) {
      rosterMemberMutation.mutate({ memberId, payload });
    },
    updateRosterMemberAsync(memberId: string, payload: OrderHandoverRosterMemberUpdatePayload) {
      return rosterMemberMutation.mutateAsync({ memberId, payload });
    },
    createRosterMember(payload: OrderHandoverRosterMemberCreatePayload) {
      rosterCreateMutation.mutate(payload);
    },
    createRosterMemberAsync(payload: OrderHandoverRosterMemberCreatePayload) {
      return rosterCreateMutation.mutateAsync(payload);
    },
    updateRosterFinalize(finalized: boolean) {
      rosterFinalizeMutation.mutate(finalized);
    },
    updateRosterFinalizeAsync(finalized: boolean) {
      return rosterFinalizeMutation.mutateAsync(finalized);
    },
    exportRoster(
      exportType: OrderHandoverRosterExportType,
      options?: OrderHandoverRosterExportOptions,
    ) {
      rosterExportMutation.mutate({ exportType, options });
    },
    setRosterKeyword,
    setRosterTypeFilter,
    setRosterClassGroupFilter,
    setRosterStoppedFilter,
  };
}

/** 将名单导入异常转换为可直接展示的失败原因。 */
function formatRosterImportError(error: unknown): string {
  if (!error) {
    return "";
  }
  if (
    isHttpClientError(error) &&
    (error.statusCode === 413 || error.code === "PAYLOAD_TOO_LARGE")
  ) {
    return "上传文件总大小超过服务端限制，请压缩或拆分文件后重试。";
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "名单导入处理失败，请检查导入内容后重试。";
}

/** 成交报价业务拒绝展示后端原因，服务异常保留稳定兜底。 */
function formatCustomerQuoteError(error: unknown): string {
  if (!error) {
    return "";
  }
  if (
    isHttpClientError(error) &&
    error.statusCode !== null &&
    error.statusCode >= 400 &&
    error.statusCode < 500 &&
    error.message.trim()
  ) {
    return error.message;
  }
  return "成交报价加载或选择失败，请稍后重试。";
}
